import { useEffect, useState } from "react";
import axiosInstance from "./axiosInstance";

// Effective client-app sidebar configuration for the logged-in tenant.
// Cached in localStorage (cleared on logout, which already calls
// localStorage.clear()) and keyed by token so a different login or an admin
// "login as user" never sees another account's menus.
const CACHE_KEY = "sidebarCfg";

const readCache = () => {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    return c && c.token === localStorage.getItem("token") ? c : null;
  } catch {
    return null;
  }
};

let state = readCache() || { token: null, disabledKeys: [], menus: [], loaded: false };
let inflight = null;
const listeners = new Set();

const publish = (next) => {
  state = next;
  listeners.forEach((l) => l(state));
};

export const loadSidebarConfig = (force = false) => {
  const token = localStorage.getItem("token");
  if (!token) return Promise.resolve(state);
  if (state.token !== token) state = { token, disabledKeys: [], menus: [], loaded: false };
  if (!force && state.loaded && Date.now() - (state.at || 0) < 60000) return Promise.resolve(state);
  if (inflight && !force) return inflight;
  inflight = axiosInstance
    .get("/sidebar/menus")
    .then(({ data }) => {
      const next = { token, disabledKeys: data.disabledKeys || [], menus: data.menus || [], loaded: true, at: Date.now() };
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(next));
      } catch {}
      publish(next);
      return next;
    })
    .catch(() => {
      // Fail open: a config outage must not hide the whole app.
      publish({ ...state, token, loaded: true, error: true });
      return state;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const invalidateSidebarConfig = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {}
  state = { token: null, disabledKeys: [], menus: [], loaded: false };
  inflight = null;
  return loadSidebarConfig(true);
};

export const isMenuEnabled = (cfg, key) => !key || !cfg.disabledKeys.includes(key);

// True when pathname belongs to a disabled menu. Query-bearing paths (lead
// pipelines sharing one route) are skipped: no route-level distinction exists.
export const isPathDisabled = (cfg, pathname) =>
  cfg.menus.some(
    (m) => m.path && !m.path.includes("?") && cfg.disabledKeys.includes(m.key) && (pathname === m.path || pathname.startsWith(`${m.path}/`))
  );

export const useSidebarConfig = () => {
  const [cfg, setCfg] = useState(state);
  useEffect(() => {
    listeners.add(setCfg);
    // Refetch when older than 60s so admin edits show up without a re-login.
    loadSidebarConfig();
    return () => listeners.delete(setCfg);
  }, []);
  // Re-sync if token changed since module load (login in same tab).
  return cfg.token === localStorage.getItem("token") ? cfg : { disabledKeys: [], menus: [], loaded: false };
};
